const BASE_MESSAGES = {
  eyebrow: "Extras & discounts",
  title: "Add extras or discounts if you want to leave them ready from day one",
  copy:
    "This step is optional. If the hotel has no extras yet, or you are not sure which ones to configure now, you can leave it empty, save once and continue.",
  loadError: "We could not load the extras and discounts draft.",
  currencyLoadError: "The hotel currency configuration could not be loaded. No monetary draft was changed; refresh and try again.",
  saveError: "We could not save the extras and discounts right now.",
  saveSuccess: "Your extras and discounts draft has been saved.",
  optionalInfoAria: "More information about extras and discounts in onboarding",
  optionalInfoText:
    "This step is optional.\n\nIf the hotel has no extras or discounts right now, or you are not sure what to configure yet, you can leave it empty, save once and continue with the onboarding.\n\nEverything defined here can still be created or adjusted later in Settings.",
  optionalNotice:
    "Optional step. If the hotel has no extras or discounts yet, or you still do not want to define them now, leave this step empty, save once and continue. You can come back to it later in Settings.",
  configuredTitle: "Configured extras and discounts",
  configuredCopy:
    "Create surcharges, discounts or mandatory add-ons only if you want to leave some commercial rules prepared from the start.",
  emptyTitle: "No extras or discounts yet",
  emptyCopy:
    "You can keep this step empty for now, or add the first extra or discount and save it as part of the onboarding draft.",
  add: "Add extra or discount",
  edit: "Edit",
  remove: "Remove",
  activate: "Activate",
  deactivate: "Deactivate",
  active: "Active",
  inactive: "Inactive",
  noDescription: "No internal description added yet.",
  modalKicker: "Commercial rule",
  modalCreateTitle: "Create extra or discount",
  modalEditTitle: "Edit extra or discount",
  modalCopy: "Define the rule, where it applies and how it should be charged.",
  modalDiscountHint:
    "Use a negative amount or percentage if you want this rule to behave as a discount.",
  modalSaveCreate: "Add to list",
  modalSaveUpdate: "Update item",
  cancel: "Cancel",
  validationName: "Name is required.",
  validationAmount: "Amount is required and must be a valid number.",
  validationCurrency: "Select a currency from the hotel catalog.",
  validationMinorUnits: (currency, minorUnits) =>
    `${currency} accepts ${minorUnits === 0 ? "whole units only" : `up to ${minorUnits} decimal places`}.`,
  commercialTitle: "Commercial setup",
  commercialCopy: "Name, amount model and a short internal description.",
  logicTitle: "Charge logic",
  logicCopy: "Decide the reservation scope and how the amount should be charged.",
  targetingTitle: "Inventory targeting",
  targetingCopy: "Limit the rule to specific room categories or room rates if needed.",
  restrictionsTitle: "Restrictions",
  restrictionsCopy: "Optional stay or occupancy boundaries for this rule.",
  switchesTitle: "Behaviour",
  switchesCopy: "Control whether the rule is inclusive, automatic or mandatory.",
  summaryTitle: "Live summary",
  summaryCopy: "Review the commercial rule before adding it to the list.",
  targetingHint:
    "Leave both selectors empty if the rule should apply across all inventory.",
  referencesHint:
    "If room categories or room rates are still missing, you can still create a rule that applies to all inventory.",
  fields: {
    name: "Name",
    description: "Description",
    amount: "Amount",
    amountType: "Amount type",
    currency: "Currency",
    scope: "Scope",
    chargePeriod: "Charge period",
    chargeUnit: "Charge unit",
    validFrom: "Valid from",
    validTo: "Valid to",
    noExpiry: "No expiry",
    roomCategories: "Room categories",
    roomRates: "Room rates",
    minNights: "Min nights",
    maxNights: "Max nights",
    minPax: "Min pax",
    maxPax: "Max pax",
    inclusive: "Inclusive (already in rate)",
    automatic: "Apply automatically",
    mandatory: "Mandatory",
  },
  placeholders: {
    name: "Breakfast supplement",
    description: "Internal note for the team",
    roomCategories: "Select one or more categories",
    roomRates: "Select one or more rates",
    currency: "Select a currency",
    noCurrencies: "No currencies configured",
    noOptions: "No matching options",
  },
  options: {
    scope: {
      per_room: "Per room reservation",
      per_reservation: "Per whole reservation",
    },
    chargePeriod: {
      per_stay: "Per stay",
      per_night: "Per night",
    },
    chargeUnit: {
      per_room: "Per room",
      per_adult: "Per adult",
      per_child: "Per child",
      per_pax: "Per pax",
    },
    amountType: {
      absolute: "Fixed amount",
      relative: "Relative (%)",
    },
  },
  summaryIntro:
    "This step is optional and can stay empty. If you do use it, save once the draft reflects what should be ready from the start.",
  summaryPoints: [
    "Extras and discounts created here are only part of the onboarding draft.",
    "You can leave the step empty if the hotel has no extras yet or if the decision is not clear now.",
    "Anything configured here can still be adjusted later in Settings after onboarding.",
  ],
  summaryConfiguredLabel: "Configured",
  summaryAutomaticLabel: "Automatic",
  summaryMandatoryLabel: "Mandatory",
  summaryInclusiveLabel: "Inclusive",
  summarySavedLabel: "Last save",
  summaryNotSaved: "Not saved yet",
  summaryAmountLabel: "Amount",
  summaryChargeLabel: "Charge",
  summaryValidityLabel: "Validity",
  summaryRestrictionsLabel: "Restrictions",
  summaryRoomCategoriesLabel: "Categories",
  summaryRoomRatesLabel: "Rates",
  summaryFlagsLabel: "Flags",
  summaryNoValidity: "No validity window",
  summaryNoRestrictions: "No stay or pax limits",
  summaryNoFlags: "No special flags",
  summaryAllInventory: "All inventory",
  summaryNoSelection: "Not limited",
  summaryFlags: {
    inclusive: "Inclusive",
    automatic: "Automatic",
    mandatory: "Mandatory",
    inactive: "Inactive",
  },
  summaryNights: (minValue, maxValue) => `Nights: ${minValue}-${maxValue}`,
  summaryPax: (minValue, maxValue) => `Pax: ${minValue}-${maxValue}`,
  summaryFrom: (value) => `From ${value}`,
  summaryUntil: (value) => `Until ${value}`,
};

const ES_MESSAGES = {
  ...BASE_MESSAGES,
  title: "Añade extras o descuentos si quieres dejarlos listos desde el primer día",
  copy:
    "Este paso es opcional. Si el hotel no tiene extras todavía, o aún no tienes claro cuáles configurar ahora, puedes dejarlo vacío, guardar una vez y seguir.",
  loadError: "No hemos podido cargar el borrador de extras y descuentos.",
  currencyLoadError: "No se ha podido cargar la configuración de monedas del hotel. No se ha modificado ningún borrador monetario; actualiza la página e inténtalo de nuevo.",
  saveError: "No hemos podido guardar extras y descuentos ahora mismo.",
  saveSuccess: "El borrador de extras y descuentos se ha guardado correctamente.",
  optionalInfoAria: "Más información sobre extras y descuentos en onboarding",
  optionalInfoText:
    "Este paso es opcional.\n\nSi el hotel no tiene extras o descuentos ahora mismo, o todavía no tienes claro qué configurar, puedes dejarlo vacío, guardar una vez y continuar con el onboarding.\n\nTodo lo que se defina aquí se podrá crear o ajustar más adelante en Settings.",
  optionalNotice:
    "Paso opcional. Si el hotel no tiene extras o descuentos todavía, o no quieres definirlos ahora, deja este paso vacío, guarda una vez y continúa. Siempre podrás volver a configurarlo más tarde en Settings.",
  configuredTitle: "Extras y descuentos configurados",
  configuredCopy:
    "Crea suplementos, descuentos o extras obligatorios solo si quieres dejar algunas reglas comerciales preparadas desde el inicio.",
  emptyTitle: "Todavía no hay extras ni descuentos",
  emptyCopy:
    "Puedes dejar este paso vacío por ahora, o añadir el primer extra o descuento y guardarlo dentro del borrador de onboarding.",
  add: "Añadir extra o descuento",
  edit: "Editar",
  remove: "Eliminar",
  activate: "Activar",
  deactivate: "Desactivar",
  active: "Activo",
  inactive: "Inactivo",
  noDescription: "Todavía no hay descripción interna.",
  modalKicker: "Regla comercial",
  modalCreateTitle: "Crear extra o descuento",
  modalEditTitle: "Editar extra o descuento",
  modalCopy: "Define la regla, dónde aplica y cómo debe cobrarse.",
  modalDiscountHint:
    "Usa un importe o porcentaje negativo si quieres que esta regla funcione como descuento.",
  modalSaveCreate: "Añadir a la lista",
  modalSaveUpdate: "Actualizar elemento",
  cancel: "Cancelar",
  validationName: "El nombre es obligatorio.",
  validationAmount: "El importe es obligatorio y debe ser un número válido.",
  validationCurrency: "Selecciona una moneda del catálogo del hotel.",
  validationMinorUnits: (currency, minorUnits) =>
    `${currency} admite ${minorUnits === 0 ? "solo unidades enteras" : `hasta ${minorUnits} decimales`}.`,
  commercialTitle: "Configuración comercial",
  commercialCopy: "Nombre, forma del importe y una breve descripción interna.",
  logicTitle: "Lógica de cobro",
  logicCopy: "Decide el alcance de la reserva y cómo debe cobrarse el importe.",
  targetingTitle: "Aplicación sobre inventario",
  targetingCopy: "Limita la regla a categorías o tarifas concretas si hace falta.",
  restrictionsTitle: "Restricciones",
  restrictionsCopy: "Límites opcionales de estancia u ocupación para esta regla.",
  switchesTitle: "Comportamiento",
  switchesCopy: "Controla si la regla es inclusiva, automática u obligatoria.",
  summaryTitle: "Resumen en directo",
  summaryCopy: "Revisa la regla comercial antes de añadirla a la lista.",
  targetingHint:
    "Deja ambos selectores vacíos si la regla debe aplicar a todo el inventario.",
  referencesHint:
    "Si aún faltan categorías o tarifas, igualmente puedes crear una regla que aplique a todo el inventario.",
  fields: {
    name: "Nombre",
    description: "Descripción",
    amount: "Importe",
    amountType: "Tipo de importe",
    currency: "Moneda",
    scope: "Alcance",
    chargePeriod: "Periodo de cobro",
    chargeUnit: "Unidad de cobro",
    validFrom: "Válido desde",
    validTo: "Válido hasta",
    noExpiry: "Sin vencimiento",
    roomCategories: "Categorías de habitación",
    roomRates: "Tarifas",
    minNights: "Noches mínimas",
    maxNights: "Noches máximas",
    minPax: "Pax mínimo",
    maxPax: "Pax máximo",
    inclusive: "Incluido en la tarifa",
    automatic: "Aplicar automáticamente",
    mandatory: "Obligatorio",
  },
  placeholders: {
    name: "Suplemento de desayuno",
    description: "Nota interna para el equipo",
    roomCategories: "Selecciona una o varias categorías",
    roomRates: "Selecciona una o varias tarifas",
    currency: "Selecciona una moneda",
    noCurrencies: "No hay monedas configuradas",
    noOptions: "No hay opciones que coincidan",
  },
  options: {
    scope: {
      per_room: "Por reserva de habitación",
      per_reservation: "Por reserva completa",
    },
    chargePeriod: {
      per_stay: "Por estancia",
      per_night: "Por noche",
    },
    chargeUnit: {
      per_room: "Por habitación",
      per_adult: "Por adulto",
      per_child: "Por niño",
      per_pax: "Por pax",
    },
    amountType: {
      absolute: "Importe fijo",
      relative: "Relativo (%)",
    },
  },
  summaryIntro:
    "Este paso es opcional y puede quedarse vacío. Si lo usas, guarda cuando el borrador refleje lo que quieres dejar listo desde el inicio.",
  summaryPoints: [
    "Los extras y descuentos creados aquí forman parte solo del borrador de onboarding.",
    "Puedes dejar el paso vacío si el hotel no tiene extras todavía o si la decisión no está clara ahora.",
    "Todo lo configurado aquí se podrá ajustar más adelante en Settings al terminar el onboarding.",
  ],
  summaryConfiguredLabel: "Configurados",
  summaryAutomaticLabel: "Automáticos",
  summaryMandatoryLabel: "Obligatorios",
  summaryInclusiveLabel: "Incluidos",
  summarySavedLabel: "Último guardado",
  summaryNotSaved: "Todavía no guardado",
  summaryAmountLabel: "Importe",
  summaryChargeLabel: "Cobro",
  summaryValidityLabel: "Validez",
  summaryRestrictionsLabel: "Restricciones",
  summaryRoomCategoriesLabel: "Categorías",
  summaryRoomRatesLabel: "Tarifas",
  summaryFlagsLabel: "Flags",
  summaryNoValidity: "Sin ventana de validez",
  summaryNoRestrictions: "Sin límites de noches o pax",
  summaryNoFlags: "Sin flags especiales",
  summaryAllInventory: "Todo el inventario",
  summaryNoSelection: "Sin limitar",
  summaryFlags: {
    inclusive: "Incluido",
    automatic: "Automático",
    mandatory: "Obligatorio",
    inactive: "Inactivo",
  },
  summaryNights: (minValue, maxValue) => `Noches: ${minValue}-${maxValue}`,
  summaryPax: (minValue, maxValue) => `Pax: ${minValue}-${maxValue}`,
  summaryFrom: (value) => `Desde ${value}`,
  summaryUntil: (value) => `Hasta ${value}`,
};

const CA_MESSAGES = {
  ...BASE_MESSAGES,
  title: "Afegeix extres o descomptes si els vols deixar preparats des del primer dia",
  copy:
    "Aquest pas és opcional. Si l'hotel encara no té extres, o encara no tens clar quins s'han de configurar ara, el pots deixar buit, desar una vegada i continuar.",
  loadError: "No hem pogut carregar l'esborrany d'extres i descomptes.",
  currencyLoadError: "No s'ha pogut carregar la configuració de monedes de l'hotel. No s'ha modificat cap esborrany monetari; actualitza la pàgina i torna-ho a provar.",
  saveError: "No hem pogut desar extres i descomptes ara mateix.",
  saveSuccess: "L'esborrany d'extres i descomptes s'ha desat correctament.",
  optionalInfoAria: "Més informació sobre extres i descomptes a l'onboarding",
  optionalInfoText:
    "Aquest pas és opcional.\n\nSi l'hotel no té extres o descomptes ara mateix, o encara no tens clar què configurar, el pots deixar buit, desar una vegada i continuar amb l'onboarding.\n\nTot el que es defineixi aquí es podrà crear o ajustar més endavant a Settings.",
  optionalNotice:
    "Pas opcional. Si l'hotel encara no té extres o descomptes, o no els vols definir ara, deixa aquest pas buit, desa una vegada i continua. Sempre ho podràs configurar més endavant a Settings.",
  configuredTitle: "Extres i descomptes configurats",
  configuredCopy:
    "Crea suplements, descomptes o extres obligatoris només si vols deixar algunes regles comercials preparades des de l'inici.",
  emptyTitle: "Encara no hi ha extres ni descomptes",
  emptyCopy:
    "Pots deixar aquest pas buit per ara, o afegir el primer extra o descompte i desar-lo dins l'esborrany d'onboarding.",
  add: "Afegir extra o descompte",
  edit: "Editar",
  remove: "Eliminar",
  activate: "Activar",
  deactivate: "Desactivar",
  active: "Actiu",
  inactive: "Inactiu",
  noDescription: "Encara no hi ha cap descripció interna.",
  modalKicker: "Regla comercial",
  modalCreateTitle: "Crear extra o descompte",
  modalEditTitle: "Editar extra o descompte",
  modalCopy: "Defineix la regla, on aplica i com s'ha de cobrar.",
  modalDiscountHint:
    "Fes servir un import o percentatge negatiu si vols que aquesta regla funcioni com a descompte.",
  modalSaveCreate: "Afegir a la llista",
  modalSaveUpdate: "Actualitzar element",
  cancel: "Cancel·lar",
  validationName: "El nom és obligatori.",
  validationAmount: "L'import és obligatori i ha de ser un número vàlid.",
  validationCurrency: "Selecciona una moneda del catàleg de l'hotel.",
  validationMinorUnits: (currency, minorUnits) =>
    `${currency} admet ${minorUnits === 0 ? "només unitats senceres" : `fins a ${minorUnits} decimals`}.`,
  commercialTitle: "Configuració comercial",
  commercialCopy: "Nom, model d'import i una breu descripció interna.",
  logicTitle: "Lògica de cobrament",
  logicCopy: "Decideix l'abast de la reserva i com s'ha de cobrar l'import.",
  targetingTitle: "Aplicació sobre inventari",
  targetingCopy: "Limita la regla a categories o tarifes concretes si cal.",
  restrictionsTitle: "Restriccions",
  restrictionsCopy: "Límits opcionals d'estada o ocupació per a aquesta regla.",
  switchesTitle: "Comportament",
  switchesCopy: "Controla si la regla és inclusiva, automàtica o obligatòria.",
  summaryTitle: "Resum en directe",
  summaryCopy: "Revisa la regla comercial abans d'afegir-la a la llista.",
  targetingHint:
    "Deixa tots dos selectors buits si la regla ha d'aplicar a tot l'inventari.",
  referencesHint:
    "Si encara falten categories o tarifes, igualment pots crear una regla que apliqui a tot l'inventari.",
  fields: {
    name: "Nom",
    description: "Descripció",
    amount: "Import",
    amountType: "Tipus d'import",
    currency: "Moneda",
    scope: "Abast",
    chargePeriod: "Període de cobrament",
    chargeUnit: "Unitat de cobrament",
    validFrom: "Vàlid des de",
    validTo: "Vàlid fins a",
    noExpiry: "Sense venciment",
    roomCategories: "Categories d'habitació",
    roomRates: "Tarifes",
    minNights: "Nits mínimes",
    maxNights: "Nits màximes",
    minPax: "Pax mínim",
    maxPax: "Pax màxim",
    inclusive: "Inclòs a la tarifa",
    automatic: "Aplicar automàticament",
    mandatory: "Obligatori",
  },
  placeholders: {
    name: "Suplement d'esmorzar",
    description: "Nota interna per a l'equip",
    roomCategories: "Selecciona una o més categories",
    roomRates: "Selecciona una o més tarifes",
    currency: "Selecciona una moneda",
    noCurrencies: "No hi ha monedes configurades",
    noOptions: "No hi ha opcions que coincideixin",
  },
  options: {
    scope: {
      per_room: "Per reserva d'habitació",
      per_reservation: "Per reserva completa",
    },
    chargePeriod: {
      per_stay: "Per estada",
      per_night: "Per nit",
    },
    chargeUnit: {
      per_room: "Per habitació",
      per_adult: "Per adult",
      per_child: "Per nen",
      per_pax: "Per pax",
    },
    amountType: {
      absolute: "Import fix",
      relative: "Relatiu (%)",
    },
  },
  summaryIntro:
    "Aquest pas és opcional i pot quedar buit. Si el fas servir, desa quan l'esborrany reflecteixi el que vols deixar preparat des de l'inici.",
  summaryPoints: [
    "Els extres i descomptes creats aquí formen part només de l'esborrany d'onboarding.",
    "Pots deixar el pas buit si l'hotel encara no té extres o si la decisió no està clara ara.",
    "Tot el que configuris aquí es podrà ajustar més endavant a Settings després de l'onboarding.",
  ],
  summaryConfiguredLabel: "Configurats",
  summaryAutomaticLabel: "Automàtics",
  summaryMandatoryLabel: "Obligatoris",
  summaryInclusiveLabel: "Inclòs",
  summarySavedLabel: "Últim desat",
  summaryNotSaved: "Encara no desat",
  summaryAmountLabel: "Import",
  summaryChargeLabel: "Cobrament",
  summaryValidityLabel: "Validesa",
  summaryRestrictionsLabel: "Restriccions",
  summaryRoomCategoriesLabel: "Categories",
  summaryRoomRatesLabel: "Tarifes",
  summaryFlagsLabel: "Flags",
  summaryNoValidity: "Sense finestra de validesa",
  summaryNoRestrictions: "Sense límits de nits o pax",
  summaryNoFlags: "Sense flags especials",
  summaryAllInventory: "Tot l'inventari",
  summaryNoSelection: "Sense limitar",
  summaryFlags: {
    inclusive: "Inclòs",
    automatic: "Automàtic",
    mandatory: "Obligatori",
    inactive: "Inactiu",
  },
  summaryNights: (minValue, maxValue) => `Nits: ${minValue}-${maxValue}`,
  summaryPax: (minValue, maxValue) => `Pax: ${minValue}-${maxValue}`,
  summaryFrom: (value) => `Des de ${value}`,
  summaryUntil: (value) => `Fins a ${value}`,
};

const MESSAGES = {
  en: BASE_MESSAGES,
  es: ES_MESSAGES,
  ca: CA_MESSAGES,
};

export function getExtrasDiscountsMessages(languageId) {
  return MESSAGES[languageId] || MESSAGES.en;
}
